import React from "react";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { API_update_company_user_profile } from "../../../../constants";
import { postRightworkAPI } from "../../requests/utilities/requests";
import { formatPhoneNumber } from "../../../../helpers/helpers";
import { formatDateReadable } from "../../requests/utilities/helpers";


// Inline date picker matching the existing employee-edit drawer style: a
// click-to-open calendar popover with the violet brand selection color.
// Click-outside detection closes the popover.
function DatePickerField({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div
      ref={ref}
      className={`relative flex-1 rounded-md text-sm cursor-pointer ${
        open
          ? "border-2 border-violet-700"
          : "border border-[#bdbdbd] hover:border-slate-900"
      }`}
    >
      <div
        className="w-full h-full px-3 py-2"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-slate-900">
            {value ? formatDateReadable(value) : "Click to select"}
          </p>
          <CalendarTodayIcon style={{ fontSize: 16 }} className="text-slate-500" />
        </div>
      </div>
      {open && (
        <div className="absolute top-full left-0 z-30 mt-1 bg-white flex items-center justify-center pl-4 pr-5 border border-[#bdbdbd] rounded-lg shadow-xl overflow-hidden">
          <div className="mt-3">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DateCalendar"]}>
                <DemoItem>
                  <DateCalendar
                    value={value ? dayjs(value) : null}
                    onChange={(newValue) => {
                      onChange(newValue ? newValue.format("YYYY-MM-DD") : "");
                      setOpen(false);
                    }}
                    slotProps={{
                      day: {
                        sx: {
                          "&.MuiPickersDay-root.Mui-selected": {
                            backgroundColor: "#6d28d9",
                          },
                        },
                      },
                    }}
                  />
                </DemoItem>
              </DemoContainer>
            </LocalizationProvider>
          </div>
        </div>
      )}
    </div>
  );
}


// Field metadata drives both the read-mode summary and the edit-mode form.
// Matches the _PROFILE_FIELDS list on the backend so we don't drift.
const FIELD_DEFS = [
  { key: "first_name", label: "First name", required: true },
  { key: "last_name", label: "Last name", required: true },
  { key: "email", label: "Email", type: "email" },
  { key: "phone_number", label: "Phone", type: "tel", display: formatPhoneNumber },
  { key: "date_of_birth", label: "Date of birth", type: "date", display: formatDateReadable },
  { key: "date_of_hire", label: "Date of hire", type: "date", display: formatDateReadable },
  { key: "emergency_contact_name", label: "Emergency contact" },
  { key: "emergency_contact_phone", label: "Emergency phone", type: "tel", display: formatPhoneNumber },
  { key: "emergency_contact_relationship", label: "Relationship" },
];

// Pull current values off the user payload. Most fields live on the user's
// STMs (we use the row's first_stm via the backend's deterministic order),
// so the page passes us an object combining User + first_stm fields.
function extractInitialValues(user) {
  const firstStm = user?.stms?.[0] || {};
  return {
    first_name: firstStm.first_name || splitName(user?.full_name)[0] || "",
    last_name: firstStm.last_name || splitName(user?.full_name)[1] || "",
    email: user?.email || firstStm.email || "",
    phone_number: user?.phone_number || firstStm.phone_number || "",
    date_of_birth: firstStm.date_of_birth || "",
    date_of_hire: firstStm.date_of_hire || "",
    emergency_contact_name: firstStm.emergency_contact_name || "",
    emergency_contact_phone: firstStm.emergency_contact_phone || "",
    emergency_contact_relationship: firstStm.emergency_contact_relationship || "",
  };
}

function splitName(fullName) {
  if (!fullName) return ["", ""];
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return ["", ""];
  if (parts.length === 1) return [parts[0], ""];
  return [parts[0], parts.slice(1).join(" ")];
}


export default function UserProfileSection({ user, companyId, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const initial = React.useMemo(() => extractInitialValues(user), [user]);
  const [values, setValues] = React.useState(initial);

  // Re-sync form when the underlying user changes (e.g. after a save → page
  // refetch → new user object lands). Doesn't reset edit-mode if the user
  // is currently in the middle of editing.
  React.useEffect(() => {
    if (!editing) setValues(initial);
  }, [initial, editing]);

  const isDirty = React.useMemo(() => {
    // Trim before comparing — the backend strips whitespace and treats
    // "John " as equivalent to "John", so trailing spaces shouldn't
    // light up the Save button as if there's a real change to write.
    return FIELD_DEFS.some(
      ({ key }) => (values[key] || "").trim() !== (initial[key] || "").trim()
    );
  }, [values, initial]);

  const beginEdit = () => {
    setValues(initial);
    setEditing(true);
  };

  const cancelEdit = () => {
    if (isDirty && !window.confirm("Discard changes?")) return;
    setValues(initial);
    setEditing(false);
  };

  const handleChange = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      // Only send fields that actually changed — keeps the audit trail
      // cleaner and lets the backend skip the email-conflict check when
      // email isn't being touched.
      const changed = {};
      FIELD_DEFS.forEach(({ key }) => {
        const next = (values[key] || "").trim();
        const prev = (initial[key] || "").trim();
        if (next !== prev) {
          changed[key] = next;
        }
      });
      if (Object.keys(changed).length === 0) {
        setEditing(false);
        return;
      }

      await postRightworkAPI(API_update_company_user_profile, {
        company_id: companyId,
        user_id: user.user_id,
        ...changed,
      });
      toast.success("Profile updated.", { autoClose: 2500 });
      setEditing(false);
      onSaved?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Failed to update profile.",
        { autoClose: 4000 },
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!editing) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-3.5">
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            Profile
          </h3>
          <button
            type="button"
            onClick={beginEdit}
            className="inline-flex items-center gap-1 text-xs text-violet-700 hover:text-violet-900"
          >
            <EditOutlinedIcon style={{ fontSize: 14 }} />
            Edit
          </button>
        </div>
        <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 gap-x-3 text-sm">
          {FIELD_DEFS.map(({ key, label, display }) => {
            const raw = values[key] || initial[key];
            const shown = raw && display ? display(raw) : raw || "—";
            return (
              <React.Fragment key={key}>
                <dt className="text-slate-500">{label}</dt>
                <dd className="text-slate-900 truncate">{shown}</dd>
              </React.Fragment>
            );
          })}
        </dl>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-violet-200 bg-violet-50/40 p-3.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-700 mb-2.5">
        Edit profile
      </h3>
      <p className="text-[11px] text-slate-500 mb-3">
        Changes propagate to all of {user?.full_name || "this user"}'s memberships in this company.
      </p>
      <div className="space-y-2">
        {FIELD_DEFS.map(({ key, label, type, required }) => (
          <div key={key}>
            <label className="block text-[11px] font-medium text-slate-600 mb-0.5">
              {label}{required && " *"}
            </label>
            {type === "date" ? (
              <DatePickerField
                value={values[key] || ""}
                onChange={(next) => setValues((prev) => ({ ...prev, [key]: next }))}
              />
            ) : (
              <TextField
                size="small"
                fullWidth
                type={type || "text"}
                value={values[key] || ""}
                onChange={handleChange(key)}
                InputProps={{
                  sx: {
                    fontSize: 13,
                    borderRadius: "6px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
                  },
                }}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={cancelEdit}
          disabled={submitting}
          className="px-3 py-1.5 rounded-md text-sm text-slate-700 hover:bg-white disabled:opacity-50"
        >
          Cancel
        </button>
        <LoadingButton
          variant="contained"
          loading={submitting}
          disabled={!isDirty || submitting}
          onClick={handleSave}
          sx={{
            textTransform: "none",
            borderRadius: "6px",
            backgroundColor: "#6d28d9",
            "&:hover": { backgroundColor: "#5b21b6" },
            fontSize: 13,
            px: 2,
            py: 0.75,
          }}
        >
          Save
        </LoadingButton>
      </div>
    </section>
  );
}
